import { useRef, useState } from "react";
import { observer } from "mobx-react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import type { Mesh } from "three";
import type { Agv, PathPoint } from "../types";
import { store } from "../../store";
import { cn } from "@/lib/utils";
import { lerp } from "three/src/math/MathUtils.js";

function interpolatePath(
    points: PathPoint[],
    progress: number,
): { x: number; z: number; angle: number } {
    if (points.length < 2) {
        return { x: points[0].x, z: points[0].z, angle: 0 };
    }

    const segments: number[] = [];
    let totalLength = 0;
    for (let i = 1; i < points.length; i++) {
        const dx = points[i].x - points[i - 1].x;
        const dz = points[i].z - points[i - 1].z;
        const len = Math.sqrt(dx * dx + dz * dz);
        segments.push(len);
        totalLength += len;
    }

    const targetDist = Math.min(Math.max(progress, 0), 1) * totalLength;
    let accumulated = 0;

    for (let i = 0; i < segments.length; i++) {
        if (accumulated + segments[i] >= targetDist) {
            const localT = segments[i] > 0 ? (targetDist - accumulated) / segments[i] : 0;
            const x = points[i].x + (points[i + 1].x - points[i].x) * localT;
            const z = points[i].z + (points[i + 1].z - points[i].z) * localT;
            const angle = Math.atan2(points[i + 1].x - points[i].x, points[i + 1].z - points[i].z);
            return { x, z, angle };
        }
        accumulated += segments[i];
    }

    const last = points[points.length - 1];
    const prev = points[points.length - 2];
    return {
        x: last.x,
        z: last.z,
        angle: Math.atan2(last.x - prev.x, last.z - prev.z),
    };
}

export const AgvFleet = observer((): JSX.Element => {
    const { agvs, warehouse, simulationRunning } = store.serviceAgv;

    useFrame((_, delta) => {
        if (!simulationRunning) return;

        for (const agv of agvs) {
            if (agv.status !== "moving" || !agv.pathId) continue;

            const path = warehouse.paths.find((p) => p.id === agv.pathId);
            if (!path) continue;

            let newProgress = agv.pathProgress + delta * 0.04;

            if (newProgress >= 1) {
                // Find next path or loop
                const pathIndex = warehouse.paths.findIndex((p) => p.id === agv.pathId);
                const nextPath = warehouse.paths[(pathIndex + 1) % warehouse.paths.length];
                newProgress = 0;
                const pos = interpolatePath(nextPath.points, 0);
                store.serviceAgv.updateAgv(agv.id, {
                    pathId: nextPath.id,
                    pathProgress: 0,
                    position: { x: pos.x, z: pos.z },
                    rotation: pos.angle,
                });
            } else {
                const pos = interpolatePath(path.points, newProgress);
                store.serviceAgv.updateAgv(agv.id, {
                    pathProgress: newProgress,
                    position: { x: pos.x, z: pos.z },
                    rotation: pos.angle,
                });
            }
        }
    });

    return (
        <group>
            {agvs.map((agv) => (
                <AgvModel key={agv.id} agv={agv} />
            ))}
        </group>
    );
});

function statusColor(status: Agv["status"]): string {
    switch (status) {
        case "errored":
            return "#ef4444";
        case "charging":
            return "#22c55e";
        default:
            return "#eab308";
    }
}

export const AgvModel = observer(({ agv }: { agv: Agv }): JSX.Element => {
    const meshRef = useRef<Mesh>(null);
    const { selectedAgvId, viewMode } = store.serviceAgv;
    const is2D = viewMode === "2d";
    const isSelected = selectedAgvId === agv.id;
    const [hovered, setHovered] = useState(false);

    useFrame((_, delta) => {
        if (!meshRef.current) return;

        // Smooth rotation
        // TODO: Probably needs a different lerp function (maybe Quaternion.slerp)
        meshRef.current.rotation.y = lerp(meshRef.current.rotation.y, agv.rotation, 10 * delta);
    });

    return (
        <group>
            <mesh
                ref={meshRef}
                position={[agv.position.x, 0.3, agv.position.z]}
                rotation={[0, 0, 0]}
                onClick={() => store.serviceAgv.setSelectedAgv(agv.id)}
                onPointerEnter={() => setHovered(true)}
                onPointerLeave={() => setHovered(false)}
            >
                <boxGeometry args={[1, 0.6, 1.5]} />
                <meshStandardMaterial
                    color={statusColor(agv.status)}
                    emissive="#ffffff"
                    emissiveIntensity={isSelected || hovered ? 0.1 : 0}
                />
                <Html
                    position={[0, 1, 0]}
                    center
                    distanceFactor={is2D ? 0.02 : 20}
                    zIndexRange={[0, 1000]}
                >
                    <div
                        className={cn(
                            `cursor-pointer rounded bg-white/80 px-2 py-1 text-xs font-semibold whitespace-nowrap text-gray-800 shadow hover:bg-gray-200`,
                            isSelected && "bg-muted",
                        )}
                        onClick={() => store.serviceAgv.setSelectedAgv(agv.id)}
                    >
                        {agv.name}
                    </div>
                </Html>
            </mesh>
        </group>
    );
});
